import { type ReactNode } from "react";
import { useAuth } from "../auth/AuthContext";
import { useIdentity } from "./useIdentity";

interface IdentityGateProps {
    children: ReactNode;
}

/**
 * Gates the main content area on identity readiness.
 *
 * Three rendering branches:
 * - jwt === null → render children. The JWT-paste modal flow handles auth.
 *   Routes that need identity should consult `useIdentity().status` directly.
 * - jwt set + identity loading → "Resolving organization..." placeholder.
 *   Protected actions in routes are blocked because identity is not ready.
 * - jwt set + identity error → error banner with a Retry button. No route
 *   content rendered.
 * - jwt set + identity ready → render children.
 *
 * The chrome (header, including the org indicator) is rendered by
 * `RootLayout` outside this gate, so the org-indicator can show its own
 * loading / error state independently.
 */
export function IdentityGate({ children }: IdentityGateProps) {
    const { jwt } = useAuth();
    const { status, error, retry } = useIdentity();

    if (jwt === null) {
        return <>{children}</>;
    }

    if (status === "loading") {
        return (
            <div className="empty-state" data-testid="identity-loading">
                <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                <span>Resolving organization…</span>
            </div>
        );
    }

    if (status === "error") {
        return (
            <div className="callout callout-error" data-testid="identity-error" role="alert">
                <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                        Could not load your organization.
                    </div>
                    <div style={{ marginBottom: 8 }}>{error?.message ?? "Unknown error."}</div>
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={retry}
                        data-testid="identity-retry"
                    >
                        <i className="fa-solid fa-rotate-right" aria-hidden="true" /> Retry
                    </button>
                </div>
            </div>
        );
    }

    return <>{children}</>;
}
